import { Project, Task, User } from '../types';
import { getLastProjectVisit, getTaskSeenAt } from './projectVisitPreferences';

export function normalizeUserId(id: string | null | undefined): string | null {
  if (id == null || id === '') return null;
  return String(id);
}

export function getActivityTimestamp(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  return Number.isNaN(ts) ? null : ts;
}

export function resolveUserId(user: User | string | null | undefined): string | null {
  if (!user) return null;
  if (typeof user === 'string') return normalizeUserId(user);
  return normalizeUserId(user.id || user._id);
}

/** Who caused the latest change on this task (server field preferred). */
export function getTaskLastActorId(task: Task): string | null {
  const fromField = resolveUserId(task.lastUpdatedBy);
  if (fromField) return fromField;

  const comments = task.comments || [];
  if (comments.length === 0) return null;

  let latest = comments[0];
  for (const comment of comments) {
    if (new Date(comment.createdAt).getTime() > new Date(latest.createdAt).getTime()) {
      latest = comment;
    }
  }

  const latestCommentTs = getActivityTimestamp(latest.createdAt);
  const updatedTs = getActivityTimestamp(task.updatedAt);
  if (latestCommentTs == null || updatedTs == null) return null;

  // Comment or nearby edit (legacy tasks without lastUpdatedBy)
  if (Math.abs(latestCommentTs - updatedTs) < 60_000) {
    return resolveUserId(latest.createdBy);
  }

  return null;
}

export function isTaskChangeByOtherUser(task: Task, userId: string | undefined): boolean {
  const selfId = normalizeUserId(userId);
  if (!selfId) return false;
  const actorId = getTaskLastActorId(task);
  if (!actorId) return false;
  return actorId !== selfId;
}

export function getProjectLastActivityAt(project: Project): Date | null {
  const externalTs = getActivityTimestamp(project.lastExternalTaskActivityAt);
  if (externalTs != null) return new Date(externalTs);
  return null;
}

export function hasProjectUpdatesSinceVisit(
  project: Project,
  userId: string | undefined,
): boolean {
  if (!userId) return false;
  if (typeof project.hasUnreadActivity === 'boolean') {
    return project.hasUnreadActivity;
  }

  const lastAck = getLastProjectVisit(userId, project._id);
  const activityAt = getProjectLastActivityAt(project);
  if (!activityAt) return false;
  if (!lastAck) return true;

  return activityAt.getTime() > lastAck.getTime();
}

export function isTaskUpdatedSinceVisit(
  task: Task,
  lastProjectVisit: Date | null,
  userId: string | undefined,
): boolean {
  if (!userId) return false;
  if (!isTaskChangeByOtherUser(task, userId)) return false;

  const updatedAt = getActivityTimestamp(task.updatedAt);
  if (updatedAt == null) return false;

  const taskSeenAt = getTaskSeenAt(userId, task._id);
  if (taskSeenAt && updatedAt <= taskSeenAt.getTime()) return false;

  const visitBaseline = lastProjectVisit ?? new Date(0);
  return updatedAt > visitBaseline.getTime();
}

export function countUpdatedTasks(
  tasks: Task[],
  lastProjectVisit: Date | null,
  userId: string | undefined,
): number {
  return tasks.filter((task) => isTaskUpdatedSinceVisit(task, lastProjectVisit, userId)).length;
}

export function formatRelativeActivityTime(iso: string | undefined | null): string | null {
  const ts = getActivityTimestamp(iso);
  if (ts == null) return null;

  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(ts).toLocaleDateString();
}
